import * as stylex from "@stylexjs/stylex";
import * as React from "react";
import { useRTL } from "../hooks";
import { colors } from "../materials/consts.stylex";
import { typography } from "../materials/typography.stylex";
const styles = {
    title: {
        k1K539: "x12nagc",
        $$css: true
    },
    selectedOptionLabelText: {
        kysU6D: "xjyslct",
        kVAEAm: "x10l6tqk",
        kbCHJM: "xu96u03",
        krVfgx: "x1ey2m1c",
        kMwMTN: "x15rks2t",
        k1xSpc: "x1lliihq",
        k3XXqK: "x1t137rt",
        kVQacm: "xb3r6kr",
        kmVPX3: "x1arfzav",
        kg5iWk: "xlyipyv",
        khDVqt: "xuxw1ft",
        kzqmXN: "xgqk73l",
        $$css: true
    },
    selectedOptionLabelTextRtl: {
        kbCHJM: "x1nx7grf",
        kCIrl2: "x3m8u43",
        $$css: true
    },
    root: {
        kVAEAm: "x1n2onr6",
        kmVPX3: "xtdwleo",
        kMzoRj: "xmkeg23",
        ksu8eU: "x1y0btm7",
        kaIpWk: "x12oqio5",
        kskxy: "xh4j8nf",
        khDVqt: "xuxw1ft",
        kVQacm: "xb3r6kr",
        kg5iWk: "xlyipyv",
        k1K539: "x1e56ztr",
        $$css: true
    },
    rootBgTransparent: {
        kWkggS: "xjbqb8w",
        $$css: true
    },
    rootBgInverted: {
        kWkggS: "xf995h5",
        $$css: true
    },
    rootBgNormal: {
        kWkggS: "x1kf733v",
        $$css: true
    },
    rootBorderInverted: {
        kVAM5u: "x1otutzv",
        $$css: true
    },
    rootBorderNormal: {
        kVAM5u: "x5snjlz",
        $$css: true
    },
    rootColorInverted: {
        kMwMTN: "x3adcck",
        $$css: true
    },
    rootColorNormal: {
        kMwMTN: "x12klvby",
        $$css: true
    },
    hoverTransparentInverted: {
        kGzVvX: "x1vm225t",
        $$css: true
    },
    hoverTransparentNormal: {
        kGzVvX: "xqy91qy",
        $$css: true
    },
    hoverOpaqueNormalEnabled: {
        kGzVvX: "x268jtm",
        $$css: true
    },
    hoverOpaqueNormalDisabled: {
        kGzVvX: "x9v0ylq",
        $$css: true
    },
    select: {
        kSiTet: "xg01cxk",
        kWkggS: "xjbqb8w",
        kzqmXN: "xh8yej3",
        kysU6D: "xjyslct",
        ksu8eU: "xng3xce",
        k3XXqK: "x1t137rt",
        kE3dHu: "x1uhho1l",
        kpe85a: "x172e5tb",
        $$css: true
    },
    selectCursorPointer: {
        kkrTdU: "x1ypdohk",
        $$css: true
    },
    selectCursorInherit: {
        kkrTdU: "xmper1u",
        $$css: true
    },
    selectMinWidthWide: {
        k7Eaqz: "x12rczxh",
        $$css: true
    },
    selectMinWidthAuto: {
        k7Eaqz: "x15x72sd",
        $$css: true
    },
    selectRtl: {
        kE3dHu: "x1igx7t4",
        kpe85a: "x1xpa7k",
        $$css: true
    }
};
function foldChoices1(options, a, f) {
    let ret = a;
    for (const option1 of options){
        if ("value" in option1) {
            ret = f(ret, option1);
        } else {
            ret = foldChoices1(option1.options, ret, f);
        }
    }
    return ret;
}
function findChoice(options, value) {
    for (const option1 of options){
        if ("value" in option1) {
            if (option1.value === value) {
                return option1;
            }
        } else {
            const opt = findChoice(option1.options, value);
            if (opt) {
                return opt;
            }
        }
    }
    return undefined;
}
export const Select = React.memo(Select_);
function Select_({ icon, value, options, onChange, title, label, position = "TOP", transparent = false, inverted = false, disabled = false }) {
    const handleOnChange = React.useCallback((v)=>{
        if (onChange) {
            const option1 = findChoice(options, v.currentTarget.value);
            if (option1) {
                onChange(option1);
            }
        }
    }, [
        options,
        onChange
    ]);
    /*
   * Forcefully disable the control if it contains fewer than two items.
   */ const numOptions = foldChoices1(options, 0, (a, _)=>a + 1);
    const isDisabled = numOptions <= 1 || disabled || false;
    const isRTL = useRTL();
    const rootBg = transparent ? styles.rootBgTransparent : inverted ? styles.rootBgInverted : styles.rootBgNormal;
    const rootBorder = inverted ? styles.rootBorderInverted : styles.rootBorderNormal;
    const rootColor = inverted ? styles.rootColorInverted : styles.rootColorNormal;
    // Only two (of the eight transparent/inverted/disabled) combos actually change background
    // on hover — the rest are no-ops that already match the base background.
    const rootHover = transparent ? isDisabled ? null : inverted ? styles.hoverTransparentInverted : styles.hoverTransparentNormal : inverted ? null : isDisabled ? styles.hoverOpaqueNormalDisabled : styles.hoverOpaqueNormalEnabled;
    return <>
      {title && <h4 {...stylex.props(typography.heading4, styles.title)}>{title}</h4>}
      <div {...stylex.props(styles.root, rootBg, rootBorder, rootColor, rootHover)} data-control-type="select">
        <select {...stylex.props(typography.table, styles.select, isDisabled ? styles.selectCursorInherit : styles.selectCursorPointer, position === "TOP" || position === "TOOLBAR" ? styles.selectMinWidthWide : styles.selectMinWidthAuto, isRTL && styles.selectRtl)} value={value} disabled={disabled} onChange={handleOnChange}>
          <option label=" "></option>
          {options.map((option1, i)=>{
        if ("value" in option1) {
            return <option key={i} value={option1.value} className="xjvpvmj">
                  {option1.label}
                </option>;
        } else {
            return <optgroup key={i} label={option1.label} className="xjvpvmj">
                  {option1.options.map((option1, j)=><option key={j} value={option1.value} className="xjvpvmj">
                      {option1.label}
                    </option>)}
                </optgroup>;
        }
    })}
        </select>

        <div className="x47corl">
          {icon && <Icon>{icon}</Icon>}
          <SelectedOptionLabel selectedOption={findChoice(options, value)} icon={!!icon} label={label}/>
          {!disabled && <Triangle/>}
        </div>
      </div>
    </>;
}
const Icon = ({ children })=>{
    return <div className="x78zum5 x6s0dn4 x10l6tqk x13vifvy x1ey2m1c x12lbrt0">
      {React.isValidElement(children) ? React.cloneElement(children, {
        className: [
            children.props.className,
            {
                className: "x1lliihq"
            }.className
        ].filter(Boolean).join(" ")
    }) : children}
    </div>;
};
const SelectedOptionLabel = ({ selectedOption, label })=>{
    const isRTL = useRTL();
    const props = stylex.props(typography.table, styles.selectedOptionLabelText, isRTL && styles.selectedOptionLabelTextRtl);
    // We want to set the label message on the title too, so the non-truncated text shows in a tooltip
    // BUT: we might get a <FormattedMessage> or something similar which may not be a string, so we have to check
    // The solution would be just to accept strings but that has bigger implications (it's not impossible though!)
    return selectedOption ? <div {...props} title={typeof selectedOption.label === "string" ? selectedOption.label : undefined}>
      {selectedOption.label}
    </div> : <div {...props} title={label}>
      {label}
    </div>;
};
const Triangle = ()=>{
    const isRTL = useRTL();
    return <svg width="10px" height="6px" viewBox="0 0 10 6" {...{
        0: {
            className: "x10l6tqk x1jz9odr x1ey2m1c xn5hqff"
        },
        1: {
            className: "x10l6tqk x1jz9odr x1ey2m1c x1qpfodn x1741ejh"
        }
    }[!!isRTL << 0]}>
      <polygon points="0 0 5 6 10 0" fill="currentColor"/>
    </svg>;
};
